import { transformRESTFloatToSDKFloat } from '@wix/sdk-runtime/transformations/float';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { resolveUrl } from '@wix/sdk-runtime/rest-modules';
import { ResolveUrlOpts } from '@wix/sdk-runtime/rest-modules';
import { RequestOptionsFactory } from '@wix/sdk-types';

function resolveWixBookingsCatalogSearchV1CatalogSearchUrl(
  opts: Omit<ResolveUrlOpts, 'domainToMappings'>
) {
  const domainToMappings = {
    _: [
      {
        srcPath: '/_api/bookings/v1/catalog/search',
        destPath: '/v1/catalog/search',
      },
    ],
    'www.wixapis.com': [
      {
        srcPath: '/bookings/catalog-search/v1/catalog/search/services/query',
        destPath: '/v1/catalog/search/services/query',
      },
    ],
    '*.dev.wix-code.com': [
      {
        srcPath: '/bookings/catalog-search/v1/catalog/search/services/query',
        destPath: '/v1/catalog/search/services/query',
      },
    ],
  };

  return resolveUrl(Object.assign(opts, { domainToMappings }));
}

const PACKAGE_NAME = '@wix/auto_sdk_bookings_catalog-search';

/**
 * Retrieves services from the catalog that match the given filters, each enriched with availability data.
 *
 * Applies location, resource type, and attribute filters, then narrows the catalog using the provided
 * WQL query. When a date range is specified in `serviceFilters`, only services with bookable slots in
 * that window are returned. Without a date range, all filtered services are returned, each marked as
 * available.
 *
 * To get complete result pages when filtering by availability, specify `query.cursorPaging`. Without it,
 * a single page of filtered results is returned without backfilling to the requested page size.
 */
export function queryServicesByFilters(
  payload: object
): RequestOptionsFactory<any> {
  function __queryServicesByFilters({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.bookings.catalog_search.v1.catalog_search',
      method: 'POST' as any,
      methodFqn:
        'wix.bookings.catalog.search.v1.CatalogSearch.QueryServicesByFilters',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixBookingsCatalogSearchV1CatalogSearchUrl({
        protoPath: '/v1/catalog/search/services/query',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'results.service.createdDate' },
              { path: 'results.service.updatedDate' },
              { path: 'results.service.media.items.image.urlExpirationDate' },
              {
                path: 'results.service.media.mainMedia.image.urlExpirationDate',
              },
              {
                path: 'results.service.media.coverMedia.image.urlExpirationDate',
              },
              {
                path: 'results.service.payment.subscription.recurringStartDate',
              },
              { path: 'results.service.bookingPolicy.createdDate' },
              { path: 'results.service.bookingPolicy.updatedDate' },
              { path: 'results.service.schedule.firstSessionStart' },
              { path: 'results.service.schedule.lastSessionEnd' },
              {
                path: 'results.service.staffMembers.mainMedia.image.urlExpirationDate',
              },
              {
                path: 'results.service.staffMemberDetails.staffMembers.mainMedia.image.urlExpirationDate',
              },
              { path: 'results.service.supportedSlugs.createdDate' },
              { path: 'results.service.mainSlug.createdDate' },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              {
                path: 'results.service.locations.business.address.geocode.latitude',
              },
              {
                path: 'results.service.locations.business.address.geocode.longitude',
              },
              {
                path: 'results.service.locations.custom.address.geocode.latitude',
              },
              {
                path: 'results.service.locations.custom.address.geocode.longitude',
              },
              {
                path: 'results.service.locations.calculatedAddress.geocode.latitude',
              },
              {
                path: 'results.service.locations.calculatedAddress.geocode.longitude',
              },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __queryServicesByFilters;
}
